import { useEffect, useState } from "react";

import { CLERK_PUBLISHABLE_KEY } from "@/config";

/**
 * Clerk names its session hint cookie per instance: `__client_uat_` plus the first eight
 * url-safe base64 characters of the SHA-1 of the publishable key. Only this build's own name is
 * read, never the bare `__client_uat`, which production and staging share on `.trovie.io`.
 * `@clerk/shared` derives the same suffix but would pull Clerk into the public entry chunk.
 */
export async function clerkSessionCookieName(publishableKey: string): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(publishableKey));
    const suffix = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .slice(0, 8);
    return `__client_uat_${suffix}`;
  } catch {
    return null;
  }
}

// Resolved once at load. Null means the name cannot be derived (no `crypto.subtle` off a secure
// origin), which is "cannot tell", not "signed out".
const cookieName: Promise<string | null> = CLERK_PUBLISHABLE_KEY
  ? clerkSessionCookieName(CLERK_PUBLISHABLE_KEY)
  : Promise.resolve(null);

/** Clerk's own predicate: absent and "0" mean signed out. A throwing cookie read cannot tell. */
function readHint(name: string): boolean {
  try {
    const prefix = `${name}=`;
    const hit = document.cookie.split(/;\s*/).find((entry) => entry.startsWith(prefix));
    return parseInt(hit?.slice(prefix.length) ?? "0", 10) > 0;
  } catch {
    return true;
  }
}

/**
 * Whether the Clerk session hint says someone may be signed in, so a public page can skip loading
 * Clerk for everyone else. False until the cookie name resolves; true wherever the hint cannot be
 * read, so the page behaves as it did before the hint existed.
 */
export function useClerkSessionHint(): boolean {
  const [hint, setHint] = useState(!CLERK_PUBLISHABLE_KEY);

  useEffect(() => {
    if (!CLERK_PUBLISHABLE_KEY) return undefined;
    let live = true;
    let name: string | null = null;
    const refresh = () => {
      if (live) setHint(name === null ? true : readHint(name));
    };
    void cookieName.then((resolved) => {
      name = resolved;
      refresh();
    });
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  return hint;
}
